import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SyntheticEvent } from 'react';
import type { AdminGateway } from '../application/adminGateway';
import type {
  AdminAccount,
  AdminAgency,
  AdminAuditEvent,
  AdminInvitation,
  AdminOverview,
  AdminEntitlement,
  AdminDependent,
  CommercialPlan,
  CreatedInvitation,
} from '../domain/admin';
import styles from './AdminScreen.module.css';

interface AdminScreenProps {
  readonly gateway: AdminGateway;
  readonly navigateToAccess?: () => void;
  readonly navigateToDashboard?: () => void;
}

type Tab = 'invitations' | 'onboarding' | 'accounts' | 'plans' | 'audit';

function defaultNavigateToAccess(): void {
  window.location.replace('/');
}

function defaultNavigateToDashboard(): void {
  window.location.assign('/dashboard');
}

function formatDate(value: string): string {
  return new Date(value).toLocaleString('es-MX');
}

export function AdminScreen({
  gateway,
  navigateToAccess = defaultNavigateToAccess,
  navigateToDashboard = defaultNavigateToDashboard,
}: AdminScreenProps): React.JSX.Element {
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [agencies, setAgencies] = useState<readonly AdminAgency[]>([]);
  const [invitations, setInvitations] = useState<readonly AdminInvitation[]>([]);
  const [accounts, setAccounts] = useState<readonly AdminAccount[]>([]);
  const [events, setEvents] = useState<readonly AdminAuditEvent[]>([]);
  const [plans, setPlans] = useState<readonly CommercialPlan[]>([]);
  const [entitlements, setEntitlements] = useState<readonly AdminEntitlement[]>([]);
  const [dependents, setDependents] = useState<readonly AdminDependent[]>([]);
  const [tab, setTab] = useState<Tab>('invitations');
  const [loading, setLoading] = useState(true);
  const [mutating, setMutating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createdInvitation, setCreatedInvitation] = useState<CreatedInvitation | null>(null);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState('agent');
  const [agencyId, setAgencyId] = useState('');
  const [expiresDays, setExpiresDays] = useState(7);
  const [entitlementProfileId, setEntitlementProfileId] = useState('');
  const [entitlementPlanKey, setEntitlementPlanKey] = useState('professional');
  const [entitlementStatus, setEntitlementStatus] = useState<'active' | 'suspended' | 'expired'>('active');
  const [dependentProfileId, setDependentProfileId] = useState('');
  const [newAgencyName, setNewAgencyName] = useState('');
  const [newAgencySlug, setNewAgencySlug] = useState('');
  const [newAgencyCity, setNewAgencyCity] = useState('');
  const [newAgencyState, setNewAgencyState] = useState('');
  const [newAgencyPhone, setNewAgencyPhone] = useState('');
  const [newAgencyWebsite, setNewAgencyWebsite] = useState('');
  const [professionalProfileId, setProfessionalProfileId] = useState('');
  const [professionalAgencyId, setProfessionalAgencyId] = useState('');
  const [professionalPublicName, setProfessionalPublicName] = useState('');
  const [professionalMembershipRole, setProfessionalMembershipRole] = useState('agent');
  const [professionalRole, setProfessionalRole] = useState('agent');

  const activeAgencies = useMemo(
    () => agencies.filter((agency) => agency.status === 'active'),
    [agencies],
  );

  const reload = useCallback(async (signal?: AbortSignal): Promise<void> => {
    const [overviewResult, agenciesResult, invitationsResult, accountsResult, auditResult, plansResult, entitlementsResult] =
      await Promise.all([
        gateway.loadOverview(signal),
        gateway.loadAgencies(signal),
        gateway.loadInvitations(signal),
        gateway.loadAccounts(signal),
        gateway.loadAudit(signal),
        gateway.loadPlans(signal),
        gateway.loadEntitlements(signal),
      ]);

    if (!overviewResult.ok) throw new Error(overviewResult.message);
    if (!agenciesResult.ok) throw new Error(agenciesResult.message);
    if (!invitationsResult.ok) throw new Error(invitationsResult.message);
    if (!accountsResult.ok) throw new Error(accountsResult.message);
    if (!auditResult.ok) throw new Error(auditResult.message);
    if (!plansResult.ok) throw new Error(plansResult.message);
    if (!entitlementsResult.ok) throw new Error(entitlementsResult.message);

    setOverview(overviewResult.overview);
    setAgencies(agenciesResult.agencies);
    setInvitations(invitationsResult.invitations);
    setAccounts(accountsResult.accounts);
    setEvents(auditResult.events);
    setPlans(plansResult.plans);
    setEntitlements(entitlementsResult.entitlements);
  }, [gateway]);

  useEffect(() => {
    const controller = new AbortController();

    void gateway.resolveCurrentIdentity(controller.signal).then(async (identity) => {
      if (!identity.ok || identity.identity.actor !== 'founder') {
        navigateToAccess();
        return;
      }

      try {
        await reload(controller.signal);
        setError(null);
      } catch (cause: unknown) {
        if (
          !controller.signal.aborted &&
          !(cause instanceof DOMException && cause.name === 'AbortError')
        ) {
          setError(
            cause instanceof Error
              ? cause.message
              : 'Administración no disponible.',
          );
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }).catch((cause: unknown) => {
      if (
        !controller.signal.aborted &&
        !(cause instanceof DOMException && cause.name === 'AbortError')
      ) {
        navigateToAccess();
      }
    });

    return () => {
      controller.abort();
    };
  }, [gateway, navigateToAccess, reload]);

  async function refreshAfterMutation(): Promise<void> {
    await reload();
    if (entitlementProfileId !== '') {
      const dependentResult = await gateway.loadDependents(entitlementProfileId);
      if (dependentResult.ok) setDependents(dependentResult.dependents);
    }
    setError(null);
  }

  useEffect(() => {
    if (entitlementProfileId === '') return;

    const controller = new AbortController();
    void gateway.loadDependents(entitlementProfileId, controller.signal).then((result) => {
      if (result.ok) setDependents(result.dependents);
    }).catch((cause: unknown) => {
      if (
        !controller.signal.aborted &&
        !(cause instanceof DOMException && cause.name === 'AbortError')
      ) {
        setError('No fue posible leer dependientes.');
      }
    });

    return () => {
      controller.abort();
    };
  }, [entitlementProfileId, gateway]);

  async function handleCreateInvitation(event: SyntheticEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setMutating('create-invitation');
    setError(null);
    setCreatedInvitation(null);

    try {
      const result = await gateway.createInvitation(
        email,
        role,
        agencyId === '' ? null : agencyId,
        expiresDays,
      );

      if (!result.ok) {
        setError(result.message);
        return;
      }

      setCreatedInvitation(result.invitation);
      setEmail('');
      await refreshAfterMutation();
    } catch {
      setError('No fue posible crear la invitación.');
    } finally {
      setMutating(null);
    }
  }

  async function handleRevoke(invitationId: string): Promise<void> {
    setMutating(invitationId);
    setError(null);
    try {
      const result = await gateway.revokeInvitation(invitationId);
      if (!result.ok) {
        setError(result.message);
        return;
      }
      await refreshAfterMutation();
    } finally {
      setMutating(null);
    }
  }

  async function handleStatus(account: AdminAccount): Promise<void> {
    const target = account.profileStatus === 'active' ? 'suspended' : 'active';
    setMutating(account.profileId);
    setError(null);
    try {
      const result = await gateway.setProfileStatus(account.profileId, target);
      if (!result.ok) {
        setError(result.message);
        return;
      }
      await refreshAfterMutation();
    } finally {
      setMutating(null);
    }
  }

  async function handleEntitlement(event: SyntheticEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (entitlementProfileId === '') return;
    setMutating('entitlement:' + entitlementProfileId);
    setError(null);
    try {
      const result = await gateway.setEntitlement(
        entitlementProfileId,
        entitlementPlanKey,
        entitlementStatus,
      );
      if (!result.ok) {
        setError(result.message);
        return;
      }
      await refreshAfterMutation();
    } finally {
      setMutating(null);
    }
  }

  async function handleDependent(
    dependentId: string,
    enabled: boolean,
  ): Promise<void> {
    if (entitlementProfileId === '') return;
    setMutating('dependent:' + dependentId);
    setError(null);
    try {
      const result = await gateway.setDependent(
        entitlementProfileId,
        dependentId,
        enabled,
      );
      if (!result.ok) {
        setError(result.message);
        return;
      }
      setDependentProfileId('');
      await refreshAfterMutation();
    } finally {
      setMutating(null);
    }
  }

  async function handleCreateAgency(
    event: SyntheticEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault();
    setMutating('create-agency');
    setError(null);
    try {
      const result = await gateway.createAgency(
        newAgencyName,
        newAgencySlug,
        newAgencyCity,
        newAgencyState,
        newAgencyPhone,
        newAgencyWebsite,
      );
      if (!result.ok) {
        setError(result.message);
        return;
      }
      setNewAgencyName('');
      setNewAgencySlug('');
      setNewAgencyCity('');
      setNewAgencyState('');
      setNewAgencyPhone('');
      setNewAgencyWebsite('');
      await refreshAfterMutation();
    } finally {
      setMutating(null);
    }
  }

  async function handleLinkProfessional(
    event: SyntheticEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault();
    if (
      professionalProfileId === '' ||
      professionalAgencyId === '' ||
      professionalPublicName.trim() === ''
    ) {
      setError('Selecciona cuenta, agencia y nombre público.');
      return;
    }

    setMutating('link-professional');
    setError(null);
    try {
      const result = await gateway.linkProfessional(
        professionalProfileId,
        professionalAgencyId,
        professionalPublicName,
        professionalMembershipRole,
        professionalRole,
      );
      if (!result.ok) {
        setError(result.message);
        return;
      }
      await refreshAfterMutation();
    } finally {
      setMutating(null);
    }
  }

  async function handleAdminRole(account: AdminAccount): Promise<void> {
    const enabled = !account.globalRoles.includes('admin');
    setMutating('role:' + account.profileId);
    setError(null);
    try {
      const result = await gateway.setGlobalAdmin(account.profileId, enabled);
      if (!result.ok) {
        setError(result.message);
        return;
      }
      await refreshAfterMutation();
    } finally {
      setMutating(null);
    }
  }

  return (
    <main className={styles.shell}>
      <header className={styles.header}>
        <button className={styles.brand} type="button" onClick={navigateToDashboard}>
          ALPHA<span>•MLS</span>
        </button>
        <span className={styles.sectionName}>Administration</span>
        <button
          className={styles.signOut}
          type="button"
          onClick={() => void gateway.signOut().finally(navigateToAccess)}
        >
          Cerrar sesión
        </button>
      </header>

      <section className={styles.hero}>
        <div>
          <p className={styles.eyebrow}>Control operativo</p>
          <h1>Administración</h1>
          <p>
            Invitaciones, cuentas, superusuarios y auditoría con acciones
            sensibles ejecutadas exclusivamente en servidor.
          </p>
        </div>
        <span className={styles.serverBadge}>Autorización server-side</span>
      </section>

      {loading ? (
        <div className={styles.loading} role="status">
          Cargando superficie administrativa…
        </div>
      ) : null}

      {error !== null ? (
        <div className={styles.error} role="alert">{error}</div>
      ) : null}

      {overview !== null ? (
        <section className={styles.metrics} aria-label="Resumen administrativo">
          <article><span>Perfiles</span><strong>{overview.profiles}</strong></article>
          <article><span>Invitaciones pendientes</span><strong>{overview.pendingInvitations}</strong></article>
          <article><span>Invitaciones redimidas</span><strong>{overview.redeemedInvitations}</strong></article>
          <article><span>Superusuarios</span><strong>{overview.superusers}</strong></article>
          <article><span>Eventos de auditoría</span><strong>{overview.auditEvents}</strong></article>
        </section>
      ) : null}

      <nav className={styles.tabs} aria-label="Secciones de administración">
        {([
          ['invitations', 'Invitaciones'],
          ['onboarding', 'Onboarding'],
          ['accounts', 'Cuentas y superusuarios'],
          ['plans', 'Planes'],
          ['audit', 'Auditoría'],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            aria-pressed={tab === key}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </nav>

      {tab === 'invitations' ? (
        <section className={styles.workspace}>
          <form className={styles.inviteForm} onSubmit={(event) => void handleCreateInvitation(event)}>
            <div className={styles.sectionHeading}>
              <div>
                <p className={styles.eyebrow}>Nueva invitación</p>
                <h2>Crear acceso seguro</h2>
              </div>
              <span>El token se muestra una sola vez.</span>
            </div>

            <label>
              Correo
              <input
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.currentTarget.value)}
                placeholder="persona@empresa.com"
              />
            </label>

            <label>
              Rol
              <select value={role} onChange={(event) => setRole(event.currentTarget.value)}>
                <option value="agent">Agent</option>
                <option value="broker">Broker</option>
                <option value="manager">Manager</option>
                <option value="owner">Owner</option>
                <option value="client">Client</option>
                <option value="admin">Admin</option>
              </select>
            </label>

            <label>
              Agencia
              <select value={agencyId} onChange={(event) => setAgencyId(event.currentTarget.value)}>
                <option value="">Sin agencia</option>
                {activeAgencies.map((agency) => (
                  <option value={agency.agencyId} key={agency.agencyId}>{agency.name}</option>
                ))}
              </select>
            </label>

            <label>
              Vigencia
              <select
                value={String(expiresDays)}
                onChange={(event) => setExpiresDays(Number(event.currentTarget.value))}
              >
                <option value="3">3 días</option>
                <option value="7">7 días</option>
                <option value="14">14 días</option>
                <option value="30">30 días</option>
              </select>
            </label>

            <button
              type="submit"
              disabled={
                mutating === 'create-invitation' ||
                (['owner', 'broker', 'manager', 'agent'].includes(role) && agencyId === '')
              }
            >
              {mutating === 'create-invitation' ? 'Creando…' : 'Crear invitación'}
            </button>
          </form>

          {createdInvitation !== null ? (
            <aside className={styles.tokenCard} aria-live="polite">
              <p className={styles.eyebrow}>Token generado</p>
              <strong>{createdInvitation.email}</strong>
              <code>{createdInvitation.token}</code>
              <small>
                Conserva correo y token juntos. La base sólo guarda el digest del secreto.
              </small>
            </aside>
          ) : null}

          <div className={styles.tableCard}>
            <div className={styles.sectionHeading}>
              <h2>Invitaciones</h2>
              <span>{invitations.length} visibles</span>
            </div>
            <div className={styles.tableScroll}>
              <table>
                <thead>
                  <tr>
                    <th>Correo</th>
                    <th>Rol</th>
                    <th>Agencia</th>
                    <th>Estado</th>
                    <th>Vence</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {invitations.map((invitation) => (
                    <tr key={invitation.invitationId}>
                      <td>{invitation.email}</td>
                      <td>{invitation.invitedRole}</td>
                      <td>{invitation.agencyName ?? '—'}</td>
                      <td><span className={styles.status}>{invitation.status}</span></td>
                      <td>{formatDate(invitation.expiresAt)}</td>
                      <td>
                        <button
                          type="button"
                          disabled={
                            invitation.status !== 'pending' ||
                            mutating === invitation.invitationId
                          }
                          onClick={() => void handleRevoke(invitation.invitationId)}
                        >
                          Revocar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      ) : null}

      {tab === 'onboarding' ? (
        <section className={styles.workspace}>
          <form
            className={styles.inviteForm}
            onSubmit={(event) => void handleCreateAgency(event)}
          >
            <div className={styles.sectionHeading}>
              <div>
                <p className={styles.eyebrow}>Estructura comercial</p>
                <h2>Crear agencia</h2>
              </div>
              <span>La primera agencia habilita onboarding profesional.</span>
            </div>

            <label>
              Nombre
              <input
                required
                value={newAgencyName}
                onChange={(event) => setNewAgencyName(event.currentTarget.value)}
              />
            </label>

            <label>
              Slug
              <input
                required
                value={newAgencySlug}
                onChange={(event) => setNewAgencySlug(event.currentTarget.value)}
                placeholder="agencia-ejemplo"
              />
            </label>

            <label>
              Ciudad
              <input
                required
                value={newAgencyCity}
                onChange={(event) => setNewAgencyCity(event.currentTarget.value)}
              />
            </label>

            <label>
              Estado
              <input
                required
                value={newAgencyState}
                onChange={(event) => setNewAgencyState(event.currentTarget.value)}
              />
            </label>

            <label>
              Teléfono
              <input
                value={newAgencyPhone}
                onChange={(event) => setNewAgencyPhone(event.currentTarget.value)}
              />
            </label>

            <label>
              Sitio
              <input
                value={newAgencyWebsite}
                onChange={(event) => setNewAgencyWebsite(event.currentTarget.value)}
                placeholder="https://"
              />
            </label>

            <button type="submit" disabled={mutating === 'create-agency'}>
              {mutating === 'create-agency' ? 'Creando…' : 'Crear agencia'}
            </button>
          </form>

          <form
            className={styles.inviteForm}
            onSubmit={(event) => void handleLinkProfessional(event)}
          >
            <div className={styles.sectionHeading}>
              <div>
                <p className={styles.eyebrow}>Perfil profesional</p>
                <h2>Vincular cuenta a agencia</h2>
              </div>
              <span>Útil para Founder o recuperación administrativa.</span>
            </div>

            <label>
              Cuenta
              <select
                required
                value={professionalProfileId}
                onChange={(event) => {
                  const profileId = event.currentTarget.value;
                  setProfessionalProfileId(profileId);
                  const account = accounts.find((candidate) => candidate.profileId === profileId);
                  setProfessionalPublicName(account?.displayName ?? '');
                }}
              >
                <option value="">Seleccionar cuenta</option>
                {accounts.map((account) => (
                  <option value={account.profileId} key={account.profileId}>
                    {account.displayName}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Agencia
              <select
                required
                value={professionalAgencyId}
                onChange={(event) => setProfessionalAgencyId(event.currentTarget.value)}
              >
                <option value="">Seleccionar agencia</option>
                {activeAgencies.map((agency) => (
                  <option value={agency.agencyId} key={agency.agencyId}>{agency.name}</option>
                ))}
              </select>
            </label>

            <label>
              Nombre público
              <input
                required
                value={professionalPublicName}
                onChange={(event) => setProfessionalPublicName(event.currentTarget.value)}
              />
            </label>

            <label>
              Rol de agencia
              <select
                value={professionalMembershipRole}
                onChange={(event) => setProfessionalMembershipRole(event.currentTarget.value)}
              >
                <option value="owner">Owner</option>
                <option value="broker">Broker</option>
                <option value="manager">Manager</option>
                <option value="agent">Agent</option>
                <option value="assistant">Assistant</option>
              </select>
            </label>

            <label>
              Rol profesional
              <select
                value={professionalRole}
                onChange={(event) => setProfessionalRole(event.currentTarget.value)}
              >
                <option value="broker">Broker</option>
                <option value="agent">Agent</option>
                <option value="associate">Associate</option>
                <option value="referral">Referral</option>
              </select>
            </label>

            <button type="submit" disabled={mutating === 'link-professional'}>
              {mutating === 'link-professional' ? 'Vinculando…' : 'Vincular perfil'}
            </button>
          </form>
        </section>
      ) : null}

      {tab === 'accounts' ? (
        <section className={styles.tableCard}>
          <div className={styles.sectionHeading}>
            <div>
              <p className={styles.eyebrow}>Cuentas invitadas y superusuarios</p>
              <h2>Perfiles autorizados</h2>
            </div>
            <span>Cambios reversibles y auditados.</span>
          </div>
          <div className={styles.tableScroll}>
            <table>
              <thead>
                <tr>
                  <th>Identidad</th>
                  <th>Agencia</th>
                  <th>Roles globales</th>
                  <th>Estado</th>
                  <th>Cuenta</th>
                  <th>Admin</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => {
                  const founder = account.globalRoles.includes('founder');
                  const admin = account.globalRoles.includes('admin');
                  const agencyLabel =
                    account.agencyName === null
                      ? '—'
                      : account.agencyRole === null
                        ? account.agencyName
                        : account.agencyName + ' · ' + account.agencyRole;
                  return (
                    <tr key={account.profileId}>
                      <td>
                        <strong>{account.displayName}</strong>
                        <small>{account.email ?? account.publicName ?? account.profileId}</small>
                      </td>
                      <td>{agencyLabel}</td>
                      <td>{account.globalRoles.length > 0 ? account.globalRoles.join(', ') : '—'}</td>
                      <td><span className={styles.status}>{account.profileStatus}</span></td>
                      <td>
                        <button
                          type="button"
                          disabled={founder || mutating === account.profileId}
                          onClick={() => void handleStatus(account)}
                        >
                          {account.profileStatus === 'active' ? 'Suspender' : 'Activar'}
                        </button>
                      </td>
                      <td>
                        <button
                          type="button"
                          disabled={founder || mutating === 'role:' + account.profileId}
                          onClick={() => void handleAdminRole(account)}
                        >
                          {admin ? 'Quitar admin' : 'Hacer admin'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {tab === 'plans' ? (
        <section className={styles.tableCard}>
          <div className={styles.sectionHeading}>
            <div>
              <p className={styles.eyebrow}>Beta comercial</p>
              <h2>Planes y entitlements</h2>
            </div>
            <span>Pagos externos durante beta; precios aún no ratificados.</span>
          </div>

          <div className={styles.planGrid}>
            {plans.map((plan) => (
              <article key={plan.planKey}>
                <span>{plan.displayName}</span>
                <strong>{plan.propertyLimit} propiedades</strong>
                <small>
                  1 titular + {plan.dependentLimit} dependiente{plan.dependentLimit === 1 ? '' : 's'} · {plan.photosPerProperty} fotos/propiedad
                </small>
                <em>{plan.paymentMode === 'external' ? 'Pago externo' : 'Pago integrado'}</em>
              </article>
            ))}
          </div>

          <form className={styles.entitlementForm} onSubmit={(event) => void handleEntitlement(event)}>
            <label>
              Titular
              <select
                required
                value={entitlementProfileId}
                onChange={(event) => {
                  const profileId = event.currentTarget.value;
                  setEntitlementProfileId(profileId);
                  setDependents([]);
                  setDependentProfileId('');
                }}
              >
                <option value="">Seleccionar perfil</option>
                {accounts
                  .filter((account) => account.publicName !== null)
                  .map((account) => (
                    <option value={account.profileId} key={account.profileId}>
                      {account.displayName}
                    </option>
                  ))}
              </select>
            </label>
            <label>
              Plan
              <select
                value={entitlementPlanKey}
                onChange={(event) => setEntitlementPlanKey(event.currentTarget.value)}
              >
                {plans.map((plan) => (
                  <option value={plan.planKey} key={plan.planKey}>{plan.displayName}</option>
                ))}
              </select>
            </label>
            <label>
              Estado
              <select
                value={entitlementStatus}
                onChange={(event) =>
                  setEntitlementStatus(
                    event.currentTarget.value as 'active' | 'suspended' | 'expired',
                  )
                }
              >
                <option value="active">Activo</option>
                <option value="suspended">Suspendido</option>
                <option value="expired">Expirado</option>
              </select>
            </label>
            <button
              type="submit"
              disabled={
                entitlementProfileId === '' ||
                mutating === 'entitlement:' + entitlementProfileId
              }
            >
              Aplicar plan
            </button>
          </form>

          <form
            className={styles.entitlementForm}
            onSubmit={(event) => {
              event.preventDefault();
              if (dependentProfileId !== '') {
                void handleDependent(dependentProfileId, true);
              }
            }}
          >
            <label>
              Dependiente profesional
              <select
                value={dependentProfileId}
                onChange={(event) => setDependentProfileId(event.currentTarget.value)}
                disabled={entitlementProfileId === ''}
              >
                <option value="">Seleccionar dependiente</option>
                {accounts
                  .filter(
                    (account) =>
                      account.publicName !== null &&
                      account.profileId !== entitlementProfileId &&
                      !dependents.some(
                        (dependent) =>
                          dependent.dependentProfileId === account.profileId &&
                          dependent.status === 'active',
                      ),
                  )
                  .map((account) => (
                    <option value={account.profileId} key={account.profileId}>
                      {account.displayName}
                    </option>
                  ))}
              </select>
            </label>
            <button
              type="submit"
              disabled={
                entitlementProfileId === '' ||
                dependentProfileId === '' ||
                mutating === 'dependent:' + dependentProfileId
              }
            >
              Agregar dependiente
            </button>
          </form>

          {entitlementProfileId !== '' ? (
            <div className={styles.dependentStrip}>
              <strong>Dependientes del titular</strong>
              {dependents.filter((dependent) => dependent.status === 'active').length === 0 ? (
                <span>Sin dependientes activos.</span>
              ) : (
                dependents
                  .filter((dependent) => dependent.status === 'active')
                  .map((dependent) => (
                    <button
                      type="button"
                      key={dependent.dependentProfileId}
                      disabled={mutating === 'dependent:' + dependent.dependentProfileId}
                      onClick={() =>
                        void handleDependent(dependent.dependentProfileId, false)
                      }
                    >
                      {dependent.displayName} · Quitar
                    </button>
                  ))
              )}
            </div>
          ) : null}

          <div className={styles.tableScroll}>
            <table>
              <thead>
                <tr>
                  <th>Titular</th>
                  <th>Plan</th>
                  <th>Dependientes</th>
                  <th>Propiedades</th>
                  <th>Fotos</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {entitlements.map((entitlement) => (
                  <tr key={entitlement.holderProfileId}>
                    <td>{entitlement.holderName}</td>
                    <td>{entitlement.planName}</td>
                    <td>{entitlement.dependentsUsed} / {entitlement.dependentLimit}</td>
                    <td>{entitlement.propertiesUsed} / {entitlement.propertyLimit}</td>
                    <td>{entitlement.photosPerProperty} por propiedad</td>
                    <td><span className={styles.status}>{entitlement.entitlementStatus}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      {tab === 'audit' ? (
        <section className={styles.tableCard}>
          <div className={styles.sectionHeading}>
            <div>
              <p className={styles.eyebrow}>Audit trail</p>
              <h2>Eventos recientes</h2>
            </div>
            <span>{events.length} eventos</span>
          </div>
          <div className={styles.auditList}>
            {events.map((event) => {
              const entityLabel =
                event.entityId === null
                  ? event.entityType
                  : event.entityType + ' · ' + event.entityId;
              return (
                <article key={event.eventId}>
                  <div>
                    <strong>{event.action}</strong>
                    <span>{entityLabel}</span>
                  </div>
                  <div>
                    <span>{event.actorName ?? 'Sistema'}</span>
                    <time dateTime={event.occurredAt}>{formatDate(event.occurredAt)}</time>
                  </div>
                  <code>{JSON.stringify(event.metadata)}</code>
                </article>
              );
            })}
          </div>
        </section>
      ) : null}
    </main>
  );
}
